import {
  Drawer,
  FocusTrap,
  TextInput,
  NumberInput,
  Anchor,
  Box,
  Center,
  Group,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './MobileMenu.module.scss';
import { ReactComponent as IconCalendar } from './img/iconCalendar.svg';
import { ReactComponent as ChevronDownIcon } from './img/iconChewron2.svg';
import { ReactComponent as IconChevronLeft } from './img/iconChewronLeft.svg';
import { ReactComponent as SearchIcon } from './img/iconSearch2.svg';
import { DatePicker } from '@mantine/dates';
import { FilterButton } from 'components/OrdersFilters/FilterButton';
import {
  useGetOrderFiltersQuery,
  useGetOrderTemplatesFiltersQuery,
  useGetTypesFiltersQuery,
} from 'redux/Api/ordersApi/ordersApi';
import { useGetEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { useGetTabPaginationBeneficiarQuery } from 'redux/Api/beneficiariesApi/beneficiariesApi';
import { OrderFilterParams } from 'hooks/useOrderFiltersNew';
import { MobileFilterGroup } from '../../components/OrderFiltersMobile/MobileFilterGroup';
import { MobilePriceFilter } from '../../components/OrderFiltersMobile/MobilePriceFilter';
import { EmployedsFilterCheckbox } from 'components/EmployedsFilters/EmployedsFilterCheckbox';
import { ActFilterData } from 'components/OrdersFilters/ActFilter';
import { ReceiptFilterData } from 'components/OrdersFilters/ReceiptFilter';
import { ReactComponent as IconFilterSettings } from './filters/iconFilterSettingts.svg';
import { ReactComponent as IconFilterSettingsActive } from './filters/iconFilterSettingtsActive.svg';
import { ReactComponent as IconCloseActive } from './filters/iconCloseFilter.svg';
import classNames from 'classnames';
import {
  FilterOption,
  MobileFilterDrawer,
} from 'components/OrderFiltersMobile/MobileFilterDrawer';
import dayjs from 'dayjs';
import { SelectedItem } from 'components/EmployedsFilters/EmployedsAdaptiveFilter';
import { ItemTypes } from 'redux/Api/beneficiariesApi/type';

interface MobileMenuProps {
  mobileTabs: any;
  handleTabClick: (val: string) => void;
  activeTabName: string;
  toggleSearchOpen: () => void;
  onApplyFilters: (filters: OrderFilterParams) => void;
  onResetFilters: () => void;
  initialFilters?: OrderFilterParams;
  resetTrigger?: number;
  setActive: (tab: string) => void;
  active: string;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  mobileTabs,
  handleTabClick,
  activeTabName,
  toggleSearchOpen,
  onApplyFilters,
  onResetFilters,
  initialFilters = {},
  resetTrigger = 0,
  setActive,
  active,
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [openedFilter, { open: openFilter, close: closeFilter }] = useDisclosure(false);
  const [openedDate, { open: openDate, close: closeDate }] = useDisclosure(false);
  const [rangeValue, setRangeValue] = useState<[Date | null, Date | null]>([null, null]);
  const [currentDateField, setCurrentDateField] = useState<string>('');
  const [filterDrawerState, setFilterDrawerState] = useState<{
    opened: boolean;
    title: string;
    options: FilterOption[];
    selectedValues: string[];
    filterType: string;
  }>({
    opened: false,
    title: '',
    options: [],
    selectedValues: [],
    filterType: '',
  });

  const { data: filtersData } = useGetOrderFiltersQuery();
  const role = localStorage.getItem('role');

  const [queryParamsTypes, setQueryParamsTypes] = useState({
    page: 1,
    per_page: 20,
    search: '',
  });

  const handleTypesServerSearch = useCallback((search: string) => {
    setQueryParamsTypes((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return { ...prev, page: 1, search };
    });
  }, []);

  const {
    data: typesFiltersData,
    isLoading: isLoadingTypes,
    isFetching: isFetchingTypes,
  } = useGetTypesFiltersQuery(
    {
      page: queryParamsTypes.page,
      per_page: queryParamsTypes.per_page,
      ...(queryParamsTypes.search ? { search: queryParamsTypes.search } : {}),
    },
    { skip: !openedFilter },
  );

  const hasNextTypesPage =
    !!typesFiltersData?.meta &&
    typesFiltersData.meta.current_page < typesFiltersData.meta.last_page;

  const loadMoreTypes = useCallback(() => {
    if (isFetchingTypes) return;
    if (!hasNextTypesPage) return;
    setQueryParamsTypes((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingTypes, hasNextTypesPage]);

  const [queryParamsBeneficiaries, setQueryParamsBeneficiaries] = useState({
    page: 1,
    per_page: 20,
    search: '',
  });

  const handleBeneficiariesServerSearch = useCallback((search: string) => {
    setQueryParamsBeneficiaries((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return { ...prev, page: 1, search };
    });
  }, []);

  const {
    data: beneficiariesFiltersData,
    isLoading: isLoadingBeneficiaries,
    isFetching: isFetchingBeneficiaries,
  } = useGetTabPaginationBeneficiarQuery(
    {
      page: queryParamsBeneficiaries.page,
      per_page: queryParamsBeneficiaries.per_page,
      ...(queryParamsBeneficiaries.search
        ? { search: queryParamsBeneficiaries.search }
        : {}),
    },
    { skip: !openedFilter || role !== 'worksupport' },
  );

  const hasNextBeneficiariesPage =
    !!beneficiariesFiltersData?.meta &&
    beneficiariesFiltersData.meta.current_page < beneficiariesFiltersData.meta.last_page;

  const loadMoreBeneficiaries = useCallback(() => {
    if (isFetchingBeneficiaries) return;
    if (!hasNextBeneficiariesPage) return;
    setQueryParamsBeneficiaries((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingBeneficiaries, hasNextBeneficiariesPage]);

  const [queryParamsTemplates, setQueryParamsTemplates] = useState({
    page: 1,
    per_page: 20,
    search: '',
  });

  const handleTemplatesServerSearch = useCallback((search: string) => {
    setQueryParamsTemplates((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return { ...prev, page: 1, search };
    });
  }, []);

  const {
    data: templatesFiltersData,
    isLoading: isLoadingTemplates,
    isFetching: isFetchingTemplates,
  } = useGetOrderTemplatesFiltersQuery(
    {
      page: queryParamsTemplates.page,
      per_page: queryParamsTemplates.per_page,
      ...(queryParamsTemplates.search ? { search: queryParamsTemplates.search } : {}),
    },
    { skip: !openedFilter },
  );

  const hasNextTemplatesPage =
    !!templatesFiltersData?.meta &&
    templatesFiltersData.meta.current_page < templatesFiltersData.meta.last_page;

  const loadMoreTemplates = useCallback(() => {
    if (isFetchingTemplates) return;
    if (!hasNextTemplatesPage) return;
    setQueryParamsTemplates((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingTemplates, hasNextTemplatesPage]);

  const [queryParamsWorkers, setQueryParamsWorkers] = useState({
    page: 1,
    per_page: 20,
    searchValue: '',
  });

  const handleWorkersServerSearch = useCallback((searchValue: string) => {
    setQueryParamsWorkers((prev) => {
      if (prev.page === 1 && prev.searchValue === searchValue) return prev;
      return { ...prev, page: 1, searchValue };
    });
  }, []);

  const {
    data: workersFiltersData,
    isLoading: isLoadingWorkers,
    isFetching: isFetchingWorkers,
  } = useGetEmployedsQuery(
    {
      page: queryParamsWorkers.page,
      per_page: queryParamsWorkers.per_page,
      searchValue: queryParamsWorkers.searchValue,
    },
    { skip: !openedFilter },
  );

  const hasNextWorkersPage =
    !!workersFiltersData?.meta &&
    workersFiltersData.meta.current_page < workersFiltersData.meta.last_page;

  const loadMoreWorkers = useCallback(() => {
    if (isFetchingWorkers) return;
    if (!hasNextWorkersPage) return;
    setQueryParamsWorkers((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingWorkers, hasNextWorkersPage]);

  const [customerIds, setCustomerIds] = useState<SelectedItem[]>(
    initialFilters.customer_ids || [],
  );
  const [typeIds, setTypeIds] = useState<SelectedItem[]>(initialFilters.type_ids || []);
  const [workerIds, setWorkerIds] = useState<SelectedItem[]>(
    initialFilters.worker_ids || [],
  );
  const [presetIds, setPresetIds] = useState<SelectedItem[]>(
    initialFilters.preset_ids || [],
  );
  const [statusIds, setStatusIds] = useState<SelectedItem[]>(
    initialFilters.statuses || [],
  );
  const [paymentTypeKeywords, setPaymentTypeKeywords] = useState<string[]>(
    (initialFilters.payment_type_ids || []).map((item) => item.id),
  );
  const prevResetTriggerRef = useRef(resetTrigger);
  const [amountFrom, setAmountFrom] = useState<number | undefined>(
    initialFilters.amount_from,
  );
  const [amountTo, setAmountTo] = useState<number | undefined>(initialFilters.amount_to);
  const [numbersRangeStart, setNumbersRangeStart] = useState<number | undefined>(
    initialFilters.numbers_range?.start !== undefined &&
      initialFilters.numbers_range.start !== ''
      ? parseInt(initialFilters.numbers_range.start, 10)
      : undefined,
  );
  const [numbersRangeEnd, setNumbersRangeEnd] = useState<number | undefined>(
    initialFilters.numbers_range?.end !== undefined &&
      initialFilters.numbers_range.end !== ''
      ? parseInt(initialFilters.numbers_range.end, 10)
      : undefined,
  );

  const [startDateStart, setStartDateStart] = useState<Date | undefined>(
    initialFilters.date_start_range?.start
      ? new Date(initialFilters.date_start_range.start)
      : undefined,
  );
  const [startDateEnd, setStartDateEnd] = useState<Date | undefined>(
    initialFilters.date_start_range?.end &&
      initialFilters.date_start_range.end !== initialFilters.date_start_range.start
      ? new Date(initialFilters.date_start_range.end)
      : initialFilters.date_start_range?.start
      ? new Date(initialFilters.date_start_range.start)
      : undefined,
  );

  const [endDateStart, setEndDateStart] = useState<Date | undefined>(
    initialFilters.date_end_range?.start
      ? new Date(initialFilters.date_end_range.start)
      : undefined,
  );
  const [endDateEnd, setEndDateEnd] = useState<Date | undefined>(
    initialFilters.date_end_range?.end &&
      initialFilters.date_end_range.end !== initialFilters.date_end_range.start
      ? new Date(initialFilters.date_end_range.end)
      : initialFilters.date_end_range?.start
      ? new Date(initialFilters.date_end_range.start)
      : undefined,
  );

  const [creationDateStart, setCreationDateStart] = useState<Date | undefined>(
    initialFilters.create_date_range?.start
      ? new Date(initialFilters.create_date_range.start)
      : undefined,
  );
  const [creationDateEnd, setCreationDateEnd] = useState<Date | undefined>(
    initialFilters.create_date_range?.end &&
      initialFilters.create_date_range.end !== initialFilters.create_date_range.start
      ? new Date(initialFilters.create_date_range.end)
      : initialFilters.create_date_range?.start
      ? new Date(initialFilters.create_date_range.start)
      : undefined,
  );
  const [paidDateStart, setPaidDateStart] = useState<Date | undefined>(
    initialFilters.date_paid_range?.start
      ? new Date(initialFilters.date_paid_range.start)
      : undefined,
  );
  const [paidDateEnd, setPaidDateEnd] = useState<Date | undefined>(
    initialFilters.date_paid_range?.end &&
      initialFilters.date_paid_range.end !== initialFilters.date_paid_range.start
      ? new Date(initialFilters.date_paid_range.end)
      : initialFilters.date_paid_range?.start
      ? new Date(initialFilters.date_paid_range.start)
      : undefined,
  );
  const [actDateStart, setActDateStart] = useState<Date | undefined>(
    initialFilters.act?.create_date_range?.start
      ? new Date(initialFilters.act.create_date_range.start)
      : undefined,
  );
  const [actDateEnd, setActDateEnd] = useState<Date | undefined>(
    initialFilters.act?.create_date_range?.end &&
      initialFilters.act.create_date_range.end !==
        initialFilters.act.create_date_range.start
      ? new Date(initialFilters.act.create_date_range.end)
      : initialFilters.act?.create_date_range?.start
      ? new Date(initialFilters.act.create_date_range.start)
      : undefined,
  );
  const [actData, setActData] = useState<ActFilterData>({
    number: initialFilters.act?.number,
    statuses: initialFilters.act?.statuses || [],
    create_date_range: initialFilters.act?.create_date_range,
  });
  const [receiptData, setReceiptData] = useState<ReceiptFilterData>({
    number: initialFilters.receipts?.number,
    statuses: initialFilters.receipts?.statuses || [],
  });

  useEffect(() => {
    setCustomerIds(initialFilters.customer_ids || []);
    setTypeIds(initialFilters.type_ids || []);
    setWorkerIds(initialFilters.worker_ids || []);
    setPresetIds(initialFilters.preset_ids || []);
    setStatusIds(initialFilters.statuses || []);
    setPaymentTypeKeywords(
      (initialFilters.payment_type_ids || []).map((item) => item.id),
    );
    setAmountFrom(initialFilters.amount_from);
    setAmountTo(initialFilters.amount_to);
    setNumbersRangeStart(
      initialFilters.numbers_range?.start !== undefined &&
        initialFilters.numbers_range.start !== ''
        ? parseInt(initialFilters.numbers_range.start, 10)
        : undefined,
    );
    setNumbersRangeEnd(
      initialFilters.numbers_range?.end !== undefined &&
        initialFilters.numbers_range.end !== ''
        ? parseInt(initialFilters.numbers_range.end, 10)
        : undefined,
    );

    setStartDateStart(
      initialFilters.date_start_range?.start
        ? new Date(initialFilters.date_start_range.start)
        : undefined,
    );
    setStartDateEnd(
      initialFilters.date_start_range?.end &&
        initialFilters.date_start_range.end !== initialFilters.date_start_range.start
        ? new Date(initialFilters.date_start_range.end)
        : initialFilters.date_start_range?.start
        ? new Date(initialFilters.date_start_range.start)
        : undefined,
    );

    setEndDateStart(
      initialFilters.date_end_range?.start
        ? new Date(initialFilters.date_end_range.start)
        : undefined,
    );
    setEndDateEnd(
      initialFilters.date_end_range?.end &&
        initialFilters.date_end_range.end !== initialFilters.date_end_range.start
        ? new Date(initialFilters.date_end_range.end)
        : initialFilters.date_end_range?.start
        ? new Date(initialFilters.date_end_range.start)
        : undefined,
    );
    setCreationDateStart(
      initialFilters.create_date_range?.start
        ? new Date(initialFilters.create_date_range.start)
        : undefined,
    );
    setCreationDateEnd(
      initialFilters.create_date_range?.end &&
        initialFilters.create_date_range.end !== initialFilters.create_date_range.start
        ? new Date(initialFilters.create_date_range.end)
        : initialFilters.create_date_range?.start
        ? new Date(initialFilters.create_date_range.start)
        : undefined,
    );
    setPaidDateStart(
      initialFilters.date_paid_range?.start
        ? new Date(initialFilters.date_paid_range.start)
        : undefined,
    );
    setPaidDateEnd(
      initialFilters.date_paid_range?.end &&
        initialFilters.date_paid_range.end !== initialFilters.date_paid_range.start
        ? new Date(initialFilters.date_paid_range.end)
        : initialFilters.date_paid_range?.start
        ? new Date(initialFilters.date_paid_range.start)
        : undefined,
    );
    setActData({
      number: initialFilters.act?.number,
      statuses: initialFilters.act?.statuses || [],
      create_date_range: initialFilters.act?.create_date_range,
    });
    setReceiptData({
      number: initialFilters.receipts?.number,
      statuses: initialFilters.receipts?.statuses || [],
    });
    setActDateStart(
      initialFilters.act?.create_date_range?.start
        ? new Date(initialFilters.act.create_date_range.start)
        : undefined,
    );
    setActDateEnd(
      initialFilters.act?.create_date_range?.end &&
        initialFilters.act.create_date_range.end !==
          initialFilters.act.create_date_range.start
        ? new Date(initialFilters.act.create_date_range.end)
        : initialFilters.act?.create_date_range?.start
        ? new Date(initialFilters.act.create_date_range.start)
        : undefined,
    );
  }, [
    initialFilters.customer_ids,
    initialFilters.type_ids,
    initialFilters.worker_ids,
    initialFilters.preset_ids,
    initialFilters.statuses,
    initialFilters.payment_type_ids,
    initialFilters.amount_from,
    initialFilters.amount_to,
    initialFilters.numbers_range?.start,
    initialFilters.numbers_range?.end,
    initialFilters.date_start_range?.start,
    initialFilters.date_start_range?.end,
    initialFilters.date_end_range?.start,
    initialFilters.date_end_range?.end,
    initialFilters.create_date_range?.start,
    initialFilters.create_date_range?.end,
    initialFilters.date_paid_range?.start,
    initialFilters.date_paid_range?.end,
    initialFilters.act?.number,
    initialFilters.act?.statuses,
    initialFilters.act?.create_date_range?.start,
    initialFilters.act?.create_date_range?.end,
    initialFilters.receipts?.number,
    initialFilters.receipts?.statuses,
  ]);

  useEffect(() => {
    if (resetTrigger > prevResetTriggerRef.current) {
      setCustomerIds([]);
      setTypeIds([]);
      setWorkerIds([]);
      setPresetIds([]);
      setStatusIds([]);
      setPaymentTypeKeywords([]);
      setAmountFrom(undefined);
      setAmountTo(undefined);
      setNumbersRangeStart(undefined);
      setNumbersRangeEnd(undefined);
      setStartDateStart(undefined);
      setStartDateEnd(undefined);
      setEndDateStart(undefined);
      setEndDateEnd(undefined);
      setCreationDateStart(undefined);
      setCreationDateEnd(undefined);
      setPaidDateStart(undefined);
      setPaidDateEnd(undefined);
      setActDateStart(undefined);
      setActDateEnd(undefined);
      setActData({
        number: undefined,
        statuses: [],
        create_date_range: undefined,
      });
      setReceiptData({
        number: undefined,
        statuses: [],
      });
    }
    prevResetTriggerRef.current = resetTrigger;
  }, [resetTrigger]);

  const transformCustomers = (): FilterOption[] => {
    return (beneficiariesFiltersData?.data || []).map((customer: ItemTypes) => ({
      id: customer.id?.toString() || '',
      label: customer.jur_name || '',
    }));
  };

  const transformTypes = (): FilterOption[] => {
    return (typesFiltersData?.items || []).map((type) => ({
      id: String(type.id),
      label: type.name,
    }));
  };

  const transformPaymentTypes = (): FilterOption[] => [
    { id: 'fixed', label: 'Фиксированная' },
    { id: 'unit_based', label: 'Оплата за выполненный объем' },
  ];

  const transformWorkers = (): FilterOption[] => {
    return (workersFiltersData?.data || []).map((worker) => ({
      id: String(worker.id),
      label:
        `${worker.surname ?? ''} ${worker.name ?? ''} ${
          worker.patronymic || ''
        }`.trim() || String(worker.id),
    }));
  };

  const transformPresets = (): FilterOption[] => {
    return (templatesFiltersData?.data || []).map((preset) => ({
      id: String(preset.id),
      label: preset.name || '',
    }));
  };

  const transformStatuses = (): FilterOption[] => {
    const result = (filtersData?.statuses || []).map((status) => ({
      id: status.name || '',
      label: status.label,
    }));
    return result;
  };

  const transformReceiptStatuses = (): FilterOption[] => {
    const result = (filtersData?.receipt_statuses || []).map((status) => ({
      id: status.name || '',
      label: status.label,
    }));
    return result;
  };

  const transformActStatuses = (): FilterOption[] => {
    const result = [
      { id: 'signed_by_customer', label: 'Подписан заказчиком' },
      { id: 'signed_by_executor', label: 'Подписан исполнителем' },
    ];
    return result;
  };

  const openFilterDrawer = (filterType: string) => {
    let options: FilterOption[] = [];
    let selectedValues: string[] = [];
    let title = '';

    switch (filterType) {
      case 'customers':
        options = transformCustomers();
        selectedValues = customerIds.map((item) => item.id);
        title = 'Заказчик';
        break;
      case 'types':
        options = transformTypes();
        selectedValues = typeIds.map((item) => item.id);
        title = 'Тип услуги';
        break;
      case 'paymentType':
        options = transformPaymentTypes();
        selectedValues = paymentTypeKeywords;
        title = 'Тип оплаты';
        break;
      case 'statuses':
        options = transformStatuses();
        selectedValues = statusIds.map((item) => item.id);
        title = 'Статус заказа';
        break;
      case 'workers':
        options = transformWorkers();
        selectedValues = workerIds.map((item) => item.id);
        title = 'Исполнитель';
        break;
      case 'presets':
        options = transformPresets();
        selectedValues = presetIds.map((item) => item.id);
        title = 'Шаблон';
        break;
      case 'receiptStatuses':
        options = transformReceiptStatuses();
        selectedValues = receiptData.statuses || [];
        title = 'Статус чека';
        break;
      case 'actStatuses':
        options = transformActStatuses();
        selectedValues = actData.statuses || [];
        title = 'Статус акта';
        break;
    }

    setFilterDrawerState({
      opened: true,
      title,
      options,
      selectedValues,
      filterType,
    });
  };

  const handleFilterDrawerChange = (values: string[]) => {
    switch (filterDrawerState.filterType) {
      case 'customers':
        setCustomerIds(
          values.map((id) => {
            const option = transformCustomers().find((opt) => opt.id === id);
            return {
              id,
              name: option?.label || customerIds.find((c) => c.id === id)?.name || '',
            };
          }),
        );
        break;
      case 'types':
        setTypeIds(
          values.map((id) => {
            const option = transformTypes().find((opt) => opt.id === id);
            return {
              id,
              name: option?.label || typeIds.find((t) => t.id === id)?.name || '',
            };
          }),
        );
        break;
      case 'paymentType':
        setPaymentTypeKeywords(values);
        break;
      case 'statuses':
        setStatusIds(
          values.map((id) => {
            const option = transformStatuses().find((opt) => opt.id === id);
            return { id, name: option?.label || '' };
          }),
        );
        break;
      case 'workers':
        setWorkerIds(
          values.map((id) => {
            const option = transformWorkers().find((opt) => opt.id === id);
            return {
              id,
              name: option?.label || workerIds.find((w) => w.id === id)?.name || '',
            };
          }),
        );
        break;
      case 'presets':
        setPresetIds(
          values.map((id) => {
            const option = transformPresets().find((opt) => opt.id === id);
            return {
              id,
              name: option?.label || presetIds.find((p) => p.id === id)?.name || '',
            };
          }),
        );
        break;
      case 'receiptStatuses':
        setReceiptData({ ...receiptData, statuses: values });
        break;
      case 'actStatuses':
        setActData({ ...actData, statuses: values });
        break;
    }

    setFilterDrawerState((prev) => ({
      ...prev,
      selectedValues: values,
    }));
  };

  const handleFilterDrawerApply = () => {
    const filters = getCurrentFilters();
    onApplyFilters(filters);
  };

  const handleFilterDrawerReset = () => {
    const filters = getCurrentFilters();

    switch (filterDrawerState.filterType) {
      case 'customers':
        delete filters.customer_ids;
        break;
      case 'types':
        delete filters.type_ids;
        break;
      case 'paymentType':
        setPaymentTypeKeywords([]);
        delete filters.payment_type_ids;
        break;
      case 'statuses':
        delete filters.statuses;
        break;
      case 'workers':
        delete filters.worker_ids;
        break;
      case 'presets':
        delete filters.preset_ids;
        break;
      case 'receiptStatuses':
        setReceiptData({ ...receiptData, statuses: [] });
        break;
      case 'actStatuses':
        setActData({ ...actData, statuses: [] });
        break;
    }

    onApplyFilters(filters);
  };

  const handleItemClick = (filterType: string, optionId: string) => {
    switch (filterType) {
      case 'customers': {
        const clickedId = optionId;
        const exists = customerIds.some((item) => item.id === clickedId);
        if (exists) {
          setCustomerIds(customerIds.filter((item) => item.id !== clickedId));
        } else {
          const option = transformCustomers().find((opt) => opt.id === clickedId);
          setCustomerIds([
            ...customerIds,
            {
              id: clickedId,
              name:
                option?.label || customerIds.find((c) => c.id === clickedId)?.name || '',
            },
          ]);
        }
        break;
      }
      case 'paymentType': {
        const clickedId = optionId;
        if (paymentTypeKeywords.includes(clickedId)) {
          setPaymentTypeKeywords([]);
        } else {
          setPaymentTypeKeywords([clickedId]);
        }
        break;
      }
      case 'types': {
        const clickedId = optionId;
        const exists = typeIds.some((item) => item.id === clickedId);
        if (exists) {
          setTypeIds(typeIds.filter((item) => item.id !== clickedId));
        } else {
          const option = transformTypes().find((opt) => opt.id === clickedId);
          setTypeIds([
            ...typeIds,
            {
              id: clickedId,
              name: option?.label || typeIds.find((t) => t.id === clickedId)?.name || '',
            },
          ]);
        }
        break;
      }
      case 'statuses': {
        const clickedId = optionId;
        const exists = statusIds.some((item) => item.id === clickedId);
        if (exists) {
          setStatusIds(statusIds.filter((item) => item.id !== clickedId));
        } else {
          const option = transformStatuses().find((opt) => opt.id === clickedId);
          setStatusIds([...statusIds, { id: clickedId, name: option?.label || '' }]);
        }
        break;
      }
      case 'workers': {
        const clickedId = optionId;
        const exists = workerIds.some((item) => item.id === clickedId);
        if (exists) {
          setWorkerIds(workerIds.filter((item) => item.id !== clickedId));
        } else {
          const option = transformWorkers().find((opt) => opt.id === clickedId);
          setWorkerIds([
            ...workerIds,
            {
              id: clickedId,
              name:
                option?.label || workerIds.find((w) => w.id === clickedId)?.name || '',
            },
          ]);
        }
        break;
      }
      case 'presets': {
        const clickedId = optionId;
        const exists = presetIds.some((item) => item.id === clickedId);
        if (exists) {
          setPresetIds(presetIds.filter((item) => item.id !== clickedId));
        } else {
          const option = transformPresets().find((opt) => opt.id === clickedId);
          setPresetIds([
            ...presetIds,
            {
              id: clickedId,
              name:
                option?.label || presetIds.find((p) => p.id === clickedId)?.name || '',
            },
          ]);
        }
        break;
      }
      case 'receiptStatuses': {
        const currentStatuses = receiptData.statuses || [];
        if (currentStatuses.includes(optionId)) {
          setReceiptData({
            ...receiptData,
            statuses: currentStatuses.filter((s) => s !== optionId),
          });
        } else {
          setReceiptData({
            ...receiptData,
            statuses: [...currentStatuses, optionId],
          });
        }
        break;
      }
      case 'actStatuses': {
        const currentStatuses = actData.statuses || [];
        if (currentStatuses.includes(optionId)) {
          setActData({
            ...actData,
            statuses: currentStatuses.filter((s) => s !== optionId),
          });
        } else {
          setActData({
            ...actData,
            statuses: [...currentStatuses, optionId],
          });
        }
        break;
      }
    }
  };

  const handleItemRemove = (filterType: string, optionId: string) => {
    handleItemClick(filterType, optionId);
  };

  const getCurrentFilters = (): OrderFilterParams => {
    const filters: OrderFilterParams = {};

    if (customerIds.length > 0) filters.customer_ids = customerIds;
    if (typeIds.length > 0) filters.type_ids = typeIds;
    if (paymentTypeKeywords.length > 0) {
      filters.payment_type_ids = paymentTypeKeywords.map((id) => {
        const option = transformPaymentTypes().find((opt) => opt.id === id);
        return { id, name: option?.label || id };
      });
    }
    if (workerIds.length > 0) filters.worker_ids = workerIds;
    if (presetIds.length > 0) filters.preset_ids = presetIds;
    if (statusIds.length > 0) filters.statuses = statusIds;
    if (amountFrom !== undefined) filters.amount_from = amountFrom;
    if (amountTo !== undefined) filters.amount_to = amountTo;

    if (numbersRangeStart !== undefined || numbersRangeEnd !== undefined) {
      filters.numbers_range = {
        start: numbersRangeStart !== undefined ? String(numbersRangeStart) : '',
        end: numbersRangeEnd !== undefined ? String(numbersRangeEnd) : '',
      };
    }

    if (startDateStart || startDateEnd) {
      filters.date_start_range = {
        start: startDateStart
          ? formatDateForAPI(startDateStart)
          : startDateEnd
          ? formatDateForAPI(startDateEnd)
          : '',
        end: startDateEnd
          ? formatDateForAPI(startDateEnd)
          : startDateStart
          ? formatDateForAPI(startDateStart)
          : '',
      };
    }

    if (endDateStart || endDateEnd) {
      filters.date_end_range = {
        start: endDateStart
          ? formatDateForAPI(endDateStart)
          : endDateEnd
          ? formatDateForAPI(endDateEnd)
          : '',
        end: endDateEnd
          ? formatDateForAPI(endDateEnd)
          : endDateStart
          ? formatDateForAPI(endDateStart)
          : '',
      };
    }

    if (creationDateStart || creationDateEnd) {
      filters.create_date_range = {
        start: creationDateStart
          ? formatDateForAPI(creationDateStart)
          : creationDateEnd
          ? formatDateForAPI(creationDateEnd)
          : '',
        end: creationDateEnd
          ? formatDateForAPI(creationDateEnd)
          : creationDateStart
          ? formatDateForAPI(creationDateStart)
          : '',
      };
    }

    if (paidDateStart || paidDateEnd) {
      filters.date_paid_range = {
        start: paidDateStart
          ? formatDateForAPI(paidDateStart)
          : paidDateEnd
          ? formatDateForAPI(paidDateEnd)
          : '',
        end: paidDateEnd
          ? formatDateForAPI(paidDateEnd)
          : paidDateStart
          ? formatDateForAPI(paidDateStart)
          : '',
      };
    }

    const actDataWithDate = { ...actData };
    if (actDateStart || actDateEnd) {
      actDataWithDate.create_date_range = {
        start: actDateStart
          ? formatDateForAPI(actDateStart)
          : actDateEnd
          ? formatDateForAPI(actDateEnd)
          : '',
        end: actDateEnd
          ? formatDateForAPI(actDateEnd)
          : actDateStart
          ? formatDateForAPI(actDateStart)
          : '',
      };
    }

    if (
      actDataWithDate.number ||
      (actDataWithDate.statuses && actDataWithDate.statuses.length > 0) ||
      actDataWithDate.create_date_range
    ) {
      filters.act = actDataWithDate;
    }

    if (
      receiptData.number !== undefined ||
      (receiptData.statuses && receiptData.statuses.length > 0)
    ) {
      filters.receipts = receiptData;
    }

    return filters;
  };

  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (date: Date | undefined): string => {
    if (!date) return '';
    return dayjs(date).format('DD.MM.YYYY');
  };

  const formatDateRangeForDisplay = (
    start: Date | undefined,
    end: Date | undefined,
  ): string => {
    if (!start && !end) return '';
    if (start && !end) return formatDateForDisplay(start);
    if (!start && end) return formatDateForDisplay(end);
    if (start && end && start.getTime() === end.getTime()) {
      return formatDateForDisplay(start);
    }
    return `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`;
  };

  const openDatePicker = (fieldType: string) => {
    setCurrentDateField(fieldType);

    switch (fieldType) {
      case 'startDate':
        setRangeValue([startDateStart || null, startDateEnd || null]);
        break;
      case 'endDate':
        setRangeValue([endDateStart || null, endDateEnd || null]);
        break;
      case 'creationDate':
        setRangeValue([creationDateStart || null, creationDateEnd || null]);
        break;
      case 'paidDate':
        setRangeValue([paidDateStart || null, paidDateEnd || null]);
        break;
      case 'actDate':
        setRangeValue([actDateStart || null, actDateEnd || null]);
        break;
    }

    openDate();
  };

  const getDatePickerTitle = (): string => {
    switch (currentDateField) {
      case 'startDate':
        return 'Дата начала';
      case 'endDate':
        return 'Дата завершения';
      case 'creationDate':
        return 'Дата создания';
      case 'paidDate':
        return 'Дата оплаты';
      case 'actDate':
        return 'Дата акта';
      default:
        return 'Выбор даты';
    }
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;

    if (customerIds.length > 0) count++;
    if (typeIds.length > 0) count++;
    if (paymentTypeKeywords.length > 0) count++;
    if (workerIds.length > 0) count++;
    if (presetIds.length > 0) count++;
    if (statusIds.length > 0) count++;
    if (amountFrom !== undefined || amountTo !== undefined) count++;
    if (numbersRangeStart !== undefined || numbersRangeEnd !== undefined) count++;
    if (startDateStart || startDateEnd) count++;
    if (endDateStart || endDateEnd) count++;
    if (creationDateStart || creationDateEnd) count++;
    if (paidDateStart || paidDateEnd) count++;
    if (actDateStart || actDateEnd) count++;
    if (actData.number) count++;
    if (actData.statuses && actData.statuses.length > 0) count++;

    if (receiptData.number) count++;
    if (receiptData.statuses && receiptData.statuses.length > 0) count++;

    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  const handleApplyFilters = () => {
    const filters = getCurrentFilters();
    onApplyFilters(filters);
    closeFilter();
  };

  const handleResetAllFilters = () => {
    setCustomerIds([]);
    setTypeIds([]);
    setWorkerIds([]);
    setPresetIds([]);
    setStatusIds([]);
    setPaymentTypeKeywords([]);
    setAmountFrom(undefined);
    setAmountTo(undefined);
    setNumbersRangeStart(undefined);
    setNumbersRangeEnd(undefined);
    setStartDateStart(undefined);
    setStartDateEnd(undefined);
    setEndDateStart(undefined);
    setEndDateEnd(undefined);
    setCreationDateStart(undefined);
    setCreationDateEnd(undefined);
    setPaidDateStart(undefined);
    setPaidDateEnd(undefined);
    setActDateStart(undefined);
    setActDateEnd(undefined);
    setActData({
      number: undefined,
      statuses: [],
      create_date_range: undefined,
    });
    setReceiptData({
      number: undefined,
      statuses: [],
    });
    onResetFilters();
  };

  return (
    <>
      <div className={styles.root}>
        <div className={styles.search} onClick={toggleSearchOpen}>
          <SearchIcon />
        </div>

        <div className={styles.selector} onClick={open}>
          <span>{activeTabName}</span>
          <ChevronDownIcon />
        </div>

        {activeFiltersCount === 0 && (
          <div className={styles.filter} onClick={openFilter}>
            <IconFilterSettings />
            <span>Фильтр</span>
          </div>
        )}

        {activeFiltersCount === 1 && (
          <div
            className={classNames(styles.filter, styles.filterActive)}
            onClick={openFilter}>
            <IconFilterSettingsActive />
            <span>Фильтр</span>
            <IconCloseActive
              style={{ marginLeft: '4px' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleResetAllFilters();
              }}
            />
          </div>
        )}

        {activeFiltersCount >= 2 && (
          <div
            className={classNames(styles.filter, styles.filterActive)}
            onClick={openFilter}>
            <IconFilterSettingsActive />
            <span>Фильтр</span>
            <span className={styles.filterCount}>{activeFiltersCount}</span>
            <IconCloseActive
              style={{ marginLeft: '4px' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleResetAllFilters();
              }}
            />
          </div>
        )}
      </div>

      <Drawer
        opened={opened}
        onClose={close}
        position='bottom'
        size={280}
        zIndex={1000}
        withCloseButton={false}
        styles={{
          content: {
            borderRadius: '13px 13px 0px 0px',
            backgroundColor: '#FFF',
            padding: '12px 16px 32px 16px',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}>
        {mobileTabs.map((tab: any, index: number) => (
          <div
            key={tab.value}
            className={`${styles.tab} ${tab.value === active ? styles.tab_active : ''} ${
              index === 0 ? styles.tab_first : ''
            } ${index === mobileTabs.length - 1 ? styles.tab_last : ''}`}
            onClick={() => {
              handleTabClick(tab.value);
              setActive(tab.value);
              close();
            }}>
            <div style={{ paddingLeft: '5px' }}>{tab.label}</div>
          </div>
        ))}
      </Drawer>

      <Drawer
        opened={openedFilter}
        onClose={closeFilter}
        styles={{
          content: {
            backgroundColor: '#FFF',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}
        position='bottom'
        size={'100%'}
        zIndex={1000}
        withCloseButton={false}>
        <FocusTrap.InitialFocus />
        <div>
          <div className={styles.header}>
            <div className={styles.filterIcon}>
              <IconChevronLeft onClick={closeFilter} />
            </div>
            <span className={styles.filterTitle}>Фильтр</span>
            <span
              className={styles.filterClear}
              onClick={() => {
                handleResetAllFilters();
                closeFilter();
              }}>
              Сбросить
            </span>
          </div>

          <div className={styles.filterGroups}>
            <div>
              <div className={styles.label}>
                <span>Номера заказов</span>
              </div>
              <MobilePriceFilter
                fromValue={numbersRangeStart}
                toValue={numbersRangeEnd}
                placeholderStart={'№ заказа'}
                placeholderEnd={'№ заказа'}
                onChange={(from, to) => {
                  setNumbersRangeStart(from);
                  setNumbersRangeEnd(to);
                }}
              />
            </div>

            {role === 'worksupport' && (
              <MobileFilterGroup
                label='Заказчик'
                options={transformCustomers()}
                selectedValues={customerIds}
                onItemClick={(optionId) => handleItemClick('customers', optionId)}
                onItemRemove={(optionId) => handleItemRemove('customers', optionId)}
                onShowAll={() => openFilterDrawer('customers')}
              />
            )}

            <MobileFilterGroup
              label='Тип услуги'
              options={transformTypes()}
              selectedValues={typeIds}
              onItemClick={(optionId) => handleItemClick('types', optionId)}
              onItemRemove={(optionId) => handleItemRemove('types', optionId)}
              onShowAll={() => openFilterDrawer('types')}
            />

            <div>
              <div className={styles.label}>
                <span>Тип оплаты</span>
              </div>
              <div className={styles.genderCheckboxRow}>
                {transformPaymentTypes().map((option) => (
                  <div key={option.id} className={styles.genderCheckboxOption}>
                    <EmployedsFilterCheckbox
                      checked={paymentTypeKeywords.includes(option.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setPaymentTypeKeywords([option.id]);
                        } else {
                          setPaymentTypeKeywords([]);
                        }
                      }}
                      label={option.label}
                    />
                  </div>
                ))}
              </div>
            </div>

            <MobileFilterGroup
              label='Статус заказа'
              options={transformStatuses()}
              selectedValues={statusIds.map((item) => item.id)}
              onItemClick={(optionId) => handleItemClick('statuses', optionId)}
              onItemRemove={(optionId) => handleItemRemove('statuses', optionId)}
              onShowAll={() => openFilterDrawer('statuses')}
            />

            <div>
              <div className={styles.label}>
                <span>Сумма заказа</span>
              </div>
              <MobilePriceFilter
                fromValue={amountFrom}
                toValue={amountTo}
                placeholderStart={'0'}
                placeholderEnd={'1 000 000'}
                onChange={(from, to) => {
                  setAmountFrom(from);
                  setAmountTo(to);
                }}
              />
            </div>

            <div>
              <div className={styles.label}>
                <span>Дата начала</span>
              </div>
              <div
                className={styles.dateButton}
                onClick={() => openDatePicker('startDate')}>
                <span className={styles.dateButtonText}>
                  {formatDateRangeForDisplay(startDateStart, startDateEnd) ||
                    'Выбери период'}
                </span>
                <IconCalendar />
              </div>
            </div>

            <div>
              <div className={styles.label}>
                <span>Дата завершения</span>
              </div>
              <div
                className={styles.dateButton}
                onClick={() => openDatePicker('endDate')}>
                <span className={styles.dateButtonText}>
                  {formatDateRangeForDisplay(endDateStart, endDateEnd) || 'Выбери период'}
                </span>
                <IconCalendar />
              </div>
            </div>

            <div>
              <div className={styles.label}>
                <span>Дата создания</span>
              </div>
              <div
                className={styles.dateButton}
                onClick={() => openDatePicker('creationDate')}>
                <span className={styles.dateButtonText}>
                  {formatDateRangeForDisplay(creationDateStart, creationDateEnd) ||
                    'Выбери период'}
                </span>
                <IconCalendar />
              </div>
            </div>

            <div>
              <div className={styles.label}>
                <span>Дата оплаты</span>
              </div>
              <div
                className={styles.dateButton}
                onClick={() => openDatePicker('paidDate')}>
                <span className={styles.dateButtonText}>
                  {formatDateRangeForDisplay(paidDateStart, paidDateEnd) ||
                    'Выбери период'}
                </span>
                <IconCalendar />
              </div>
            </div>

            <MobileFilterGroup
              label='Исполнитель'
              options={transformWorkers()}
              selectedValues={workerIds}
              onItemClick={(optionId) => handleItemClick('workers', optionId)}
              onItemRemove={(optionId) => handleItemRemove('workers', optionId)}
              onShowAll={() => openFilterDrawer('workers')}
            />

            <MobileFilterGroup
              label='Шаблон'
              options={transformPresets()}
              selectedValues={presetIds}
              onItemClick={(optionId) => handleItemClick('presets', optionId)}
              onItemRemove={(optionId) => handleItemRemove('presets', optionId)}
              onShowAll={() => openFilterDrawer('presets')}
            />

            <div>
              <div className={styles.label}>
                <span>№ чека</span>
              </div>
              <TextInput
                placeholder='Введи номер чека'
                className={styles.input}
                value={receiptData.number || ''}
                onChange={(e) =>
                  setReceiptData({ ...receiptData, number: e.target.value })
                }
              />
            </div>

            <MobileFilterGroup
              label='Статус чека'
              options={transformReceiptStatuses()}
              selectedValues={receiptData.statuses || []}
              onItemClick={(optionId) => handleItemClick('receiptStatuses', optionId)}
              onItemRemove={(optionId) => handleItemRemove('receiptStatuses', optionId)}
              onShowAll={() => openFilterDrawer('receiptStatuses')}
            />

            <div>
              <div className={styles.label}>
                <span>№ акта</span>
              </div>
              <NumberInput
                placeholder='Введи номер акта'
                className={styles.input}
                value={actData.number || ''}
                onChange={(value) =>
                  setActData({ ...actData, number: String(value || '') })
                }
                min={1}
                max={100000}
                hideControls
              />
            </div>

            <MobileFilterGroup
              label='Статус акта'
              options={transformActStatuses()}
              selectedValues={actData.statuses || []}
              onItemClick={(optionId) => handleItemClick('actStatuses', optionId)}
              onItemRemove={(optionId) => handleItemRemove('actStatuses', optionId)}
              onShowAll={() => openFilterDrawer('actStatuses')}
            />

            <div>
              <div className={styles.label}>
                <span>Дата акта</span>
              </div>
              <div
                className={styles.dateButton}
                onClick={() => openDatePicker('actDate')}>
                <span className={styles.dateButtonText}>
                  {formatDateRangeForDisplay(actDateStart, actDateEnd) || 'Выбери период'}
                </span>
                <IconCalendar />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <FilterButton variant='apply' onClick={handleApplyFilters}>
            Применить
          </FilterButton>
        </div>
      </Drawer>

      <Drawer
        opened={openedDate}
        onClose={closeDate}
        styles={{
          content: {
            borderRadius: '13px 13px 0px 0px',
            backgroundColor: '#FFF',
            padding: '12px 16px 32px 16px',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}
        position='bottom'
        size={520}
        zIndex={1000}
        withCloseButton={false}>
        <Box>
          <Group justify='space-between'>
            <Text fz='20' lh='24px' fw='600' c='#121922'>
              {getDatePickerTitle()}
            </Text>
            <Anchor
              component='button'
              fz='14'
              lh='18px'
              c={'#002CFB'}
              underline='never'
              onClick={() => {
                setRangeValue([null, null]);

                switch (currentDateField) {
                  case 'startDate':
                    setStartDateStart(undefined);
                    setStartDateEnd(undefined);
                    break;
                  case 'endDate':
                    setEndDateStart(undefined);
                    setEndDateEnd(undefined);
                    break;
                  case 'creationDate':
                    setCreationDateStart(undefined);
                    setCreationDateEnd(undefined);
                    break;
                  case 'paidDate':
                    setPaidDateStart(undefined);
                    setPaidDateEnd(undefined);
                    break;
                  case 'actDate':
                    setActDateStart(undefined);
                    setActDateEnd(undefined);
                    break;
                }

                closeDate();
              }}>
              Сбросить
            </Anchor>
          </Group>
          <div className={styles.badgeContainer}>
            <div
              className={styles.badge}
              onClick={() => {
                const today = dayjs().toDate();
                setRangeValue([today, today]);

                switch (currentDateField) {
                  case 'startDate':
                    setStartDateStart(today);
                    setStartDateEnd(today);
                    break;
                  case 'endDate':
                    setEndDateStart(today);
                    setEndDateEnd(today);
                    break;
                  case 'creationDate':
                    setCreationDateStart(today);
                    setCreationDateEnd(today);
                    break;
                  case 'paidDate':
                    setPaidDateStart(today);
                    setPaidDateEnd(today);
                    break;
                  case 'actDate':
                    setActDateStart(today);
                    setActDateEnd(today);
                    break;
                }

                closeDate();
              }}>
              Сегодня
            </div>
            <div
              className={styles.badge}
              onClick={() => {
                const weekStart = dayjs().startOf('week').toDate();
                const weekEnd = dayjs().endOf('week').toDate();
                setRangeValue([weekStart, weekEnd]);

                switch (currentDateField) {
                  case 'startDate':
                    setStartDateStart(weekStart);
                    setStartDateEnd(weekEnd);
                    break;
                  case 'endDate':
                    setEndDateStart(weekStart);
                    setEndDateEnd(weekEnd);
                    break;
                  case 'creationDate':
                    setCreationDateStart(weekStart);
                    setCreationDateEnd(weekEnd);
                    break;
                  case 'paidDate':
                    setPaidDateStart(weekStart);
                    setPaidDateEnd(weekEnd);
                    break;
                  case 'actDate':
                    setActDateStart(weekStart);
                    setActDateEnd(weekEnd);
                    break;
                }

                closeDate();
              }}>
              Неделя
            </div>
            <div
              className={styles.badge}
              onClick={() => {
                const monthStart = dayjs().startOf('month').toDate();
                const monthEnd = dayjs().endOf('month').toDate();
                setRangeValue([monthStart, monthEnd]);

                switch (currentDateField) {
                  case 'startDate':
                    setStartDateStart(monthStart);
                    setStartDateEnd(monthEnd);
                    break;
                  case 'endDate':
                    setEndDateStart(monthStart);
                    setEndDateEnd(monthEnd);
                    break;
                  case 'creationDate':
                    setCreationDateStart(monthStart);
                    setCreationDateEnd(monthEnd);
                    break;
                  case 'paidDate':
                    setPaidDateStart(monthStart);
                    setPaidDateEnd(monthEnd);
                    break;
                  case 'actDate':
                    setActDateStart(monthStart);
                    setActDateEnd(monthEnd);
                    break;
                }

                closeDate();
              }}>
              Месяц
            </div>
            <div
              className={styles.badge}
              onClick={() => {
                setRangeValue([null, null]);

                switch (currentDateField) {
                  case 'startDate':
                    setStartDateStart(undefined);
                    setStartDateEnd(undefined);
                    break;
                  case 'endDate':
                    setEndDateStart(undefined);
                    setEndDateEnd(undefined);
                    break;
                  case 'creationDate':
                    setCreationDateStart(undefined);
                    setCreationDateEnd(undefined);
                    break;
                  case 'paidDate':
                    setPaidDateStart(undefined);
                    setPaidDateEnd(undefined);
                    break;
                  case 'actDate':
                    setActDateStart(undefined);
                    setActDateEnd(undefined);
                    break;
                }

                closeDate();
              }}>
              Все время
            </div>
          </div>
          <Center>
            <DatePicker
              size='lg'
              type='range'
              value={rangeValue}
              onChange={(value) => {
                const newValue = value as [Date | null, Date | null];
                setRangeValue(newValue);

                if (newValue[0] && newValue[1]) {
                  const [start, end] = newValue;
                  const effectiveStart = start || undefined;
                  const effectiveEnd = end || start || undefined;

                  switch (currentDateField) {
                    case 'startDate':
                      setStartDateStart(effectiveStart);
                      setStartDateEnd(effectiveEnd);
                      break;
                    case 'endDate':
                      setEndDateStart(effectiveStart);
                      setEndDateEnd(effectiveEnd);
                      break;
                    case 'creationDate':
                      setCreationDateStart(effectiveStart);
                      setCreationDateEnd(effectiveEnd);
                      break;
                    case 'paidDate':
                      setPaidDateStart(effectiveStart);
                      setPaidDateEnd(effectiveEnd);
                      break;
                    case 'actDate':
                      setActDateStart(effectiveStart);
                      setActDateEnd(effectiveEnd);
                      break;
                  }

                  closeDate();
                }
              }}
              highlightToday
              allowSingleDateInRange
            />
          </Center>
        </Box>
      </Drawer>

      <MobileFilterDrawer
        opened={filterDrawerState.opened}
        onClose={() => setFilterDrawerState((prev) => ({ ...prev, opened: false }))}
        title={filterDrawerState.title}
        options={
          filterDrawerState.filterType === 'customers'
            ? transformCustomers()
            : filterDrawerState.filterType === 'types'
            ? transformTypes()
            : filterDrawerState.filterType === 'workers'
            ? transformWorkers()
            : filterDrawerState.filterType === 'presets'
            ? transformPresets()
            : filterDrawerState.options
        }
        selectedValues={filterDrawerState.selectedValues}
        onChange={handleFilterDrawerChange}
        onApply={handleFilterDrawerApply}
        onReset={handleFilterDrawerReset}
        showSearch={
          filterDrawerState.filterType === 'customers' ||
          filterDrawerState.filterType === 'types' ||
          filterDrawerState.filterType === 'workers' ||
          filterDrawerState.filterType === 'presets'
            ? true
            : filterDrawerState.options.length > 6
        }
        serverList={
          filterDrawerState.filterType === 'customers' ||
          filterDrawerState.filterType === 'types' ||
          filterDrawerState.filterType === 'workers' ||
          filterDrawerState.filterType === 'presets'
        }
        onServerListSearch={
          filterDrawerState.filterType === 'customers'
            ? handleBeneficiariesServerSearch
            : filterDrawerState.filterType === 'types'
            ? handleTypesServerSearch
            : filterDrawerState.filterType === 'workers'
            ? handleWorkersServerSearch
            : filterDrawerState.filterType === 'presets'
            ? handleTemplatesServerSearch
            : undefined
        }
        serverListHasNext={
          (filterDrawerState.filterType === 'customers' && hasNextBeneficiariesPage) ||
          (filterDrawerState.filterType === 'types' && hasNextTypesPage) ||
          (filterDrawerState.filterType === 'workers' && hasNextWorkersPage) ||
          (filterDrawerState.filterType === 'presets' && hasNextTemplatesPage)
        }
        onServerListLoadMore={
          filterDrawerState.filterType === 'customers'
            ? loadMoreBeneficiaries
            : filterDrawerState.filterType === 'types'
            ? loadMoreTypes
            : filterDrawerState.filterType === 'workers'
            ? loadMoreWorkers
            : filterDrawerState.filterType === 'presets'
            ? loadMoreTemplates
            : undefined
        }
        serverListFetching={
          (filterDrawerState.filterType === 'customers' && isFetchingBeneficiaries) ||
          (filterDrawerState.filterType === 'types' && isFetchingTypes) ||
          (filterDrawerState.filterType === 'workers' && isFetchingWorkers) ||
          (filterDrawerState.filterType === 'presets' && isFetchingTemplates)
        }
        serverListLoading={
          (filterDrawerState.filterType === 'customers' && isLoadingBeneficiaries) ||
          (filterDrawerState.filterType === 'types' && isLoadingTypes) ||
          (filterDrawerState.filterType === 'workers' && isLoadingWorkers) ||
          (filterDrawerState.filterType === 'presets' && isLoadingTemplates)
        }
      />
    </>
  );
};
